import { Box, Divider, Paper, Typography } from "@mui/material"
import { DataNode } from "graphql/types"
import { useStoreState } from "hooks/storeHooks"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"

type TextBlockDiffPreviewProps = {
  dataNode: DataNode
}

export const TextBlockDiffPreview = ({ dataNode }: TextBlockDiffPreviewProps) => {
  const { textBlocks } = useStoreState((state) => state)

  // Helper function to get textblock name by ID
  const getTextBlockName = (textBlockId: string): string => {
    const textBlock = textBlocks.find((tb) => tb.id === textBlockId)
    return textBlock?.name || "Unbekannter Textblock"
  }

  // Helper function to get original DataNode
  const getOriginalDataNode = (dataNode: DataNode): DataNode | null => {
    if (!dataNode.originalTextblockId || dataNode.originalTextBlockDataNodeNo === undefined) {
      return null
    }

    const textBlock = textBlocks.find((tb) => tb.id === dataNode.originalTextblockId)
    if (!textBlock || dataNode.originalTextBlockDataNodeNo === null) {
      return null
    }

    return textBlock.block[dataNode.originalTextBlockDataNodeNo] || null
  }

  // Helper function to create character-level diff for words
  const createWordDiff = (originalWord: string, currentWord: string) => {
    if (originalWord === currentWord) {
      return <span>{originalWord}</span>
    }

    // Find longest common subsequence of characters
    const findLCS = (str1: string, str2: string) => {
      const m = str1.length
      const n = str2.length
      const dp = Array(m + 1)
        .fill(null)
        .map(() => Array(n + 1).fill(0))

      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          if (str1[i - 1] === str2[j - 1]) {
            dp[i][j] = dp[i - 1][j - 1] + 1
          } else {
            dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
          }
        }
      }

      const lcs = []
      let i = m,
        j = n
      while (i > 0 && j > 0) {
        if (str1[i - 1] === str2[j - 1]) {
          lcs.unshift({ char: str1[i - 1], originalIndex: i - 1, currentIndex: j - 1 })
          i--
          j--
        } else if (dp[i - 1][j] > dp[i][j - 1]) {
          i--
        } else {
          j--
        }
      }

      return lcs
    }

    const lcs = findLCS(originalWord, currentWord)
    const diff = []
    let originalIndex = 0
    let currentIndex = 0
    let lcsIndex = 0

    while (originalIndex < originalWord.length || currentIndex < currentWord.length) {
      if (
        lcsIndex < lcs.length &&
        originalIndex === lcs[lcsIndex].originalIndex &&
        currentIndex === lcs[lcsIndex].currentIndex
      ) {
        // Character is in both words - no change
        diff.push(<span key={`same-${originalIndex}`}>{originalWord[originalIndex]}</span>)
        originalIndex++
        currentIndex++
        lcsIndex++
      } else if (originalIndex >= originalWord.length) {
        // Only current characters left - additions
        diff.push(
          <span
            key={`add-${currentIndex}`}
            style={{
              backgroundColor: "#4caf50",
              padding: "0px 1px",
              borderRadius: "1px",
            }}
          >
            {currentWord[currentIndex]}
          </span>
        )
        currentIndex++
      } else if (currentIndex >= currentWord.length) {
        // Only original characters left - deletions
        diff.push(
          <span
            key={`del-${originalIndex}`}
            style={{
              textDecoration: "line-through",
              backgroundColor: "#f44336",
              padding: "0px 1px",
              borderRadius: "1px",
            }}
          >
            {originalWord[originalIndex]}
          </span>
        )
        originalIndex++
      } else {
        // Find next LCS match
        const currentOriginalIndex = originalIndex
        const currentCurrentIndex = currentIndex
        const nextLcsMatch = lcs.find(
          (l) => l.originalIndex >= currentOriginalIndex && l.currentIndex >= currentCurrentIndex
        )

        if (nextLcsMatch && nextLcsMatch.originalIndex === currentOriginalIndex) {
          // Addition
          diff.push(
            <span
              key={`add-${currentCurrentIndex}`}
              style={{
                backgroundColor: "#4caf50",
                padding: "0px 1px",
                borderRadius: "1px",
              }}
            >
              {currentWord[currentCurrentIndex]}
            </span>
          )
          currentIndex++
        } else {
          // Deletion
          diff.push(
            <span
              key={`del-${currentOriginalIndex}`}
              style={{
                textDecoration: "line-through",
                backgroundColor: "#f44336",
                padding: "0px 1px",
                borderRadius: "1px",
              }}
            >
              {originalWord[currentOriginalIndex]}
            </span>
          )
          originalIndex++
        }
      }
    }

    return <span>{diff}</span>
  }

  // Helper function to create text diff using word-level comparison with character-level diffs
  const createTextDiff = (original: string, current: string) => {
    if (original === current) return null

    // Split into words while preserving whitespace
    const originalWords = original.split(/(\s+)/)
    const currentWords = current.split(/(\s+)/)

    // Find longest common subsequence (LCS) of words
    const findLCS = (words1: string[], words2: string[]) => {
      const m = words1.length
      const n = words2.length
      const dp = Array(m + 1)
        .fill(null)
        .map(() => Array(n + 1).fill(0))

      // Build LCS table
      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          if (words1[i - 1] === words2[j - 1]) {
            dp[i][j] = dp[i - 1][j - 1] + 1
          } else {
            dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
          }
        }
      }

      // Backtrack to find LCS
      const lcs = []
      let i = m,
        j = n
      while (i > 0 && j > 0) {
        if (words1[i - 1] === words2[j - 1]) {
          lcs.unshift({ word: words1[i - 1], originalIndex: i - 1, currentIndex: j - 1 })
          i--
          j--
        } else if (dp[i - 1][j] > dp[i][j - 1]) {
          i--
        } else {
          j--
        }
      }

      return lcs
    }

    const lcs = findLCS(originalWords, currentWords)

    // Create diff based on LCS
    const diff = []
    let originalIndex = 0
    let currentIndex = 0
    let lcsIndex = 0

    while (originalIndex < originalWords.length || currentIndex < currentWords.length) {
      // Check if we're at an LCS match
      if (
        lcsIndex < lcs.length &&
        originalIndex === lcs[lcsIndex].originalIndex &&
        currentIndex === lcs[lcsIndex].currentIndex
      ) {
        // This word is in both texts - check for character-level changes
        const originalWord = originalWords[originalIndex]
        const currentWord = currentWords[currentIndex]

        if (originalWord === currentWord) {
          // Word is identical
          diff.push(<span key={`same-${originalIndex}`}>{originalWord}</span>)
        } else {
          // Word has character-level changes
          diff.push(
            <span key={`word-diff-${originalIndex}`}>
              {createWordDiff(originalWord, currentWord)}
            </span>
          )
        }
        originalIndex++
        currentIndex++
        lcsIndex++
      } else if (originalIndex >= originalWords.length) {
        // Only current words left - these are additions
        diff.push(
          <span
            key={`add-${currentIndex}`}
            style={{
              backgroundColor: "#4caf50",
              padding: "1px 2px",
              borderRadius: "2px",
            }}
          >
            {currentWords[currentIndex]}
          </span>
        )
        currentIndex++
      } else if (currentIndex >= currentWords.length) {
        // Only original words left - these are deletions
        diff.push(
          <span
            key={`del-${originalIndex}`}
            style={{
              textDecoration: "line-through",
              backgroundColor: "#f44336",
              padding: "1px 2px",
              borderRadius: "2px",
            }}
          >
            {originalWords[originalIndex]}
          </span>
        )
        originalIndex++
      } else {
        // Find next LCS match to determine if this is a deletion or addition
        const currentOriginalIndex = originalIndex
        const currentCurrentIndex = currentIndex
        const nextLcsMatch = lcs.find(
          (l) => l.originalIndex >= currentOriginalIndex && l.currentIndex >= currentCurrentIndex
        )

        if (nextLcsMatch && nextLcsMatch.originalIndex === currentOriginalIndex) {
          // This is an addition (current word not in original)
          diff.push(
            <span
              key={`add-${currentCurrentIndex}`}
              style={{
                backgroundColor: "#4caf50",
                padding: "1px 2px",
                borderRadius: "2px",
              }}
            >
              {currentWords[currentCurrentIndex]}
            </span>
          )
          currentIndex++
        } else {
          // This is a deletion (original word not in current)
          diff.push(
            <span
              key={`del-${currentOriginalIndex}`}
              style={{
                textDecoration: "line-through",
                backgroundColor: "#f44336",
                padding: "1px 2px",
                borderRadius: "2px",
              }}
            >
              {originalWords[currentOriginalIndex]}
            </span>
          )
          originalIndex++
        }
      }
    }

    return diff
  }

  // Only show if this DataNode comes from a textblock and has changes
  if (!dataNode.originalTextblockId || dataNode.originalTextblockId === "") {
    return null
  }

  const originalDataNode = getOriginalDataNode(dataNode)
  if (!originalDataNode) {
    return null
  }

  const textBlockName = getTextBlockName(dataNode.originalTextblockId)

  // Check if there are any changes
  const hasChanges =
    dataNode.dataType !== originalDataNode.dataType ||
    (isNodeText(dataNode) &&
      isNodeText(originalDataNode) &&
      dataNode.text !== originalDataNode.text) ||
    (isNodeTable(dataNode) &&
      isNodeTable(originalDataNode) &&
      (dataNode.tableCaption !== originalDataNode.tableCaption ||
        JSON.stringify(dataNode.tableHeader) !== JSON.stringify(originalDataNode.tableHeader) ||
        JSON.stringify(dataNode.tableBody) !== JSON.stringify(originalDataNode.tableBody))) ||
    (isNodeImage(dataNode) &&
      isNodeImage(originalDataNode) &&
      (JSON.stringify(dataNode.image) !== JSON.stringify(originalDataNode.image) ||
        JSON.stringify(dataNode.imageCaption) !== JSON.stringify(originalDataNode.imageCaption)))

  if (!hasChanges) {
    return null
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
        Änderungen gegenüber Textvorlage "{textBlockName}":
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {isNodeText(dataNode) &&
        isNodeText(originalDataNode) &&
        dataNode.text !== originalDataNode.text && (
          <Box
            sx={{
              p: 2,

              whiteSpace: "pre-wrap",
            }}
          >
            {createTextDiff(originalDataNode.text, dataNode.text)}
          </Box>
        )}

      {isNodeTable(dataNode) && isNodeTable(originalDataNode) && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
            Tabelle:
          </Typography>
          <Box sx={{ p: 2, borderRadius: 1, border: "1px solid #e0e0e0" }}>
            {dataNode.tableCaption !== originalDataNode.tableCaption && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Beschriftung:
                </Typography>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  {createTextDiff(originalDataNode.tableCaption, dataNode.tableCaption)}
                </Box>
              </Box>
            )}

            {JSON.stringify(dataNode.tableHeader) !==
              JSON.stringify(originalDataNode.tableHeader) && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Tabellenkopf geändert
                </Typography>
                <Typography variant="caption" color="warning.dark">
                  (Detaillierte Anzeige der Tabellenänderungen folgt in einer zukünftigen Version)
                </Typography>
              </Box>
            )}

            {JSON.stringify(dataNode.tableBody) !== JSON.stringify(originalDataNode.tableBody) && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Tabelleninhalt geändert
                </Typography>
                <Typography variant="caption" color="warning.dark">
                  (Detaillierte Anzeige der Tabellenänderungen folgt in einer zukünftigen Version)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {isNodeImage(dataNode) && isNodeImage(originalDataNode) && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
            Bilder:
          </Typography>
          <Box sx={{ p: 2, borderRadius: 1, border: "1px solid #e0e0e0" }}>
            {JSON.stringify(dataNode.image) !== JSON.stringify(originalDataNode.image) && (
              <Typography variant="body2" color="warning.dark">
                Bilder geändert (Anzahl: {originalDataNode.image.length} → {dataNode.image.length})
              </Typography>
            )}

            {JSON.stringify(dataNode.imageCaption) !==
              JSON.stringify(originalDataNode.imageCaption) && (
              <Typography variant="body2" color="warning.dark">
                Bildbeschriftungen geändert
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  )
}
